import {useFormContext} from "react-hook-form";
import {useState, useRef, useId} from "react";
import {cn} from "@/lib/utils";
import {Calendar, X} from "lucide-react";

type Unit = "Days" | "Weeks" | "Months";

function addTime(amount: number, unit: Unit): string {
    const d = new Date();
    if (unit === "Days") d.setDate(d.getDate() + amount);
    if (unit === "Weeks") d.setDate(d.getDate() + amount * 7);
    if (unit === "Months") d.setMonth(d.getMonth() + amount);
    return d.toISOString().split("T")[0];
}

const formatDateDisplay = (dateStr: string): string => {
    if (!dateStr) return "";
    try {
        return new Date(dateStr).toLocaleDateString("en-IN", {
            day: "2-digit", month: "short", year: "numeric",
        });
    } catch {
        return dateStr;
    }
};

// showPicker() isn't supported on older Safari/iPadOS — fall back to focus,
// which still opens the native picker on those browsers.
const openDatePicker = (input: HTMLInputElement | null) => {
    if (!input) return;
    if (typeof input.showPicker === "function") input.showPicker();
    else input.focus();
};

export default function NextVisitSection() {
    const {setValue, watch} = useFormContext();
    const selectedDate: string = watch("next_visit")?.date || "";

    const [amount, setAmount] = useState("");
    const [unit, setUnit] = useState<Unit>("Days");
    const dateRef = useRef<HTMLInputElement>(null);

    const handleAmountChange = (val: string, currentUnit: Unit) => {
        setAmount(val);
        const n = parseInt(val);
        if (!n || n <= 0) {
            setValue("next_visit.date", "", {shouldDirty: true});
            return;
        }
        setValue("next_visit.date", addTime(n, currentUnit), {shouldDirty: true});
    };

    const handleUnitChange = (newUnit: Unit) => {
        setUnit(newUnit);
        const n = parseInt(amount);
        if (n && n > 0) {
            setValue("next_visit.date", addTime(n, newUnit), {shouldDirty: true});
        }
    };

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue("next_visit.date", e.target.value, {shouldDirty: true});
        setAmount("");
    };

    const handleClear = () => {
        setValue("next_visit.date", "", {shouldDirty: true});
        setAmount("");
    };

    return (
        <div className="flex flex-wrap items-center gap-2 text-xs">

            {/* No of — wide standalone input */}
            <input
                type="number"
                min="1"
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value, unit)}
                placeholder="No of"
                className="h-9 w-40 px-3 rounded-md border border-border bg-card outline-none text-xs placeholder:text-muted-foreground/40 placeholder:font-normal focus:border-ring focus:ring-1 focus:ring-ring/20 transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />

            <div className="flex gap-1">
                {(["Days", "Weeks", "Months"] as Unit[]).map((u) => (
                    <button
                        key={u}
                        type="button"
                        onClick={() => handleUnitChange(u)}
                        className={cn(
                            "h-9 px-3 rounded-md text-xs border transition-all duration-150",
                            u === unit
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-muted text-muted-foreground border-border hover:border-primary/40 hover:text-foreground"
                        )}
                    >
                        {u}
                    </button>
                ))}
            </div>

            <span className="text-muted-foreground text-xs">Or</span>

            {/* Select date — wide, calendar icon left */}
            <div className="flex items-center gap-2">
                <div
                    onClick={() => openDatePicker(dateRef.current)}
                    className="flex items-center gap-2 h-9 px-3 w-44 rounded-md border border-border bg-[hsl(var(--input))] hover:bg-accent cursor-pointer transition-colors bg-card"
                >
                    <Calendar className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0"/>
                    {selectedDate ? (
                        <span className="text-xs font-medium text-foreground truncate">
                            {formatDateDisplay(selectedDate)}
                        </span>
                    ) : (
                        <span className="text-xs text-muted-foreground">Select date</span>
                    )}
                </div>

                <input
                    ref={dateRef}
                    type="date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    min={new Date().toISOString().split("T")[0]}
                    className="w-0 h-0 absolute opacity-0 pointer-events-none"
                />

                {selectedDate && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="p-1 rounded-full text-muted-foreground hover:bg-accent hover:text-foreground"
                    >
                        <X className="w-3 h-3"/>
                    </button>
                )}
            </div>
        </div>
    );
}